import React, { useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { mergeClassNames } from '@v2/utils/Helpers';
import './RangePicker.scss';
import { Button, Typography } from '@notch-ordering/ui-components';
import ChevronDownIcon from '@icons/chevron-down-icon.svg';
import { format, isValid, parse } from 'date-fns';
import { formatIsoStringToUtcDate } from '@/utils/DateUtils';
import { useOnClickOutside } from '@/shared/hooks/useClickOutside';

const MANUAL_DATE_INPUT_FORMATS = [
    'yyyy-MM-dd',
    'yyyy/MM/dd',
    'MM/dd/yyyy',
    'M/d/yyyy',
    'MM-dd-yyyy',
    'M-d-yyyy',
    'MMMM d, yyyy',
    'MMM d, yyyy',
    'dd-MMM-yyyy',
    'd-MMM-yyyy',
];

export const isManualDateInputComplete = (value: string): boolean => {
    const trimmedValue = value.trim();

    return [
        /^\d{4}[-/]\d{2}[-/]\d{2}$/,
        /^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/,
        /^[A-Za-z]+\.? \d{1,2}, \d{4}$/,
        /^\d{1,2}-[A-Za-z]{3}-\d{4}$/,
    ].some((pattern) => pattern.test(trimmedValue));
};

export const formatManualDateInputValue = (date?: Date): string => (date && isValid(date) ? format(date, 'yyyy-MM-dd') : '');

export const normalizeInitialDateForPicker = (date?: Date): Date | null => {
    if (!date || !isValid(date)) return null;

    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export const parseManualDateInputValue = (value: string): Date | null => {
    const trimmedValue = value.trim();

    if (!trimmedValue || !isManualDateInputComplete(trimmedValue)) return null;

    let matchedDate: Date | null = null;

    MANUAL_DATE_INPUT_FORMATS.some((dateFormat) => {
        const parsedDate = parse(trimmedValue, dateFormat, new Date());

        if (isValid(parsedDate)) {
            matchedDate = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
            return true;
        }

        return false;
    });

    if (matchedDate) return matchedDate;

    if (/[a-z]/i.test(trimmedValue)) {
        const parsedDate = new Date(trimmedValue);

        if (isValid(parsedDate)) {
            return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
        }
    }

    return null;
};

export type DateRangePickerProps = {
    initialStartDate?: Date,
    initialEndDate?: Date,
    onDateRangeChange?: (startDate: Date, endDate: Date) => void,
    className?: string,
    onClose?: () => void,
    isOpen?: boolean,
    enableManualEntry?: boolean,
};

export const DateRangePicker:React.FC<DateRangePickerProps> = ({
    initialStartDate,
    initialEndDate,
    onDateRangeChange,
    className,
    isOpen,
    onClose,
    enableManualEntry,

}) => {
    const ref = useRef(null);
    const initialStartDateForPicker = normalizeInitialDateForPicker(initialStartDate);
    const initialEndDateForPicker = normalizeInitialDateForPicker(initialEndDate);

    const [startDate, setStartDate] = useState(initialStartDateForPicker);
    const [endDate, setEndDate] = useState(initialEndDateForPicker);
    const [startDateInputValue, setStartDateInputValue] = useState(formatManualDateInputValue(initialStartDateForPicker));
    const [endDateInputValue, setEndDateInputValue] = useState(formatManualDateInputValue(initialEndDateForPicker));

    const commitManualDateRange = (): void => {
        const parsedStartDate = parseManualDateInputValue(startDateInputValue);
        const parsedEndDate = parseManualDateInputValue(endDateInputValue);

        setStartDate(parsedStartDate);
        setEndDate(parsedEndDate);

        if (parsedStartDate && parsedEndDate) {
            parsedEndDate.setHours(23, 59, 59, 999);
            onDateRangeChange?.(parsedStartDate, parsedEndDate);
        }
    };

    const onChange = (dates):void => {
        // These dates are in local time
        const [start, end] = dates;
        setStartDate(start);
        end?.setHours(23, 59, 59, 999);
        setEndDate(end);
        setStartDateInputValue(formatManualDateInputValue(start));
        setEndDateInputValue(formatManualDateInputValue(end));
        if (start && end) {
            onDateRangeChange(start, end);
        }
    };

    useOnClickOutside(ref, () => {
        onClose();
    });

    return isOpen ? (
        <div className={mergeClassNames('flex gap-4 flex-col', className)} ref={ref}>
            {enableManualEntry && <div className="flex flex-col gap-2 px-4">
                <label className="flex flex-col gap-1">
                    <Typography variant="BASE" className="text-gray-600 mb-0">Start date</Typography>
                    <input
                        className="border border-gray-300 rounded-md px-2 py-1.5 text-2 focus:border-teal-250 focus:outline-none"
                        placeholder="YYYY-MM-DD"
                        value={startDateInputValue}
                        onChange={(e): void => {
                            setStartDateInputValue(e.target.value);
                        }}
                        onBlur={commitManualDateRange}
                        onKeyDown={(e): void => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                commitManualDateRange();
                            }
                        }}/>
                </label>
                <label className="flex flex-col gap-1">
                    <Typography variant="BASE" className="text-gray-600 mb-0">End date</Typography>
                    <input
                        className="border border-gray-300 rounded-md px-2 py-1.5 text-2 focus:border-teal-250 focus:outline-none"
                        placeholder="YYYY-MM-DD"
                        value={endDateInputValue}
                        onChange={(e): void => {
                            setEndDateInputValue(e.target.value);
                        }}
                        onBlur={commitManualDateRange}
                        onKeyDown={(e): void => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                commitManualDateRange();
                            }
                        }}/>
                </label>
            </div>}
            <DatePicker
                renderCustomHeader={({
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                    monthDate,

                }):JSX.Element => (
                    <div className="flex items-center justify-between">
                        <Typography variant="LG-1" weight="font-medium" className="text-gray-700 pl-3.5">{formatIsoStringToUtcDate(monthDate.toISOString(), 'MMMM, YYY')}</Typography>
                        <div className="flex gap-3 ml-auto mr-2">
                            <Button className="p-0"
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                variant="ICON"
                                size="NO_BACKGROUND">
                                <ChevronDownIcon className="w-4 h-4 text-gray-600 rotate-90"/>
                            </Button>
                            <Button className="p-0"
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                variant="ICON"
                                size="NO_BACKGROUND">
                                <ChevronDownIcon className="w-4 h-4 text-gray-600 -rotate-90"/>
                            </Button>

                        </div>

                    </div>
                )}
                selected={startDate}
                onChange={onChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange

                inline/>

        </div>
    ) : null;
};
